import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale } from "../contexts/LocaleContext";
import { Card, CardContent, Typography, Box, Button, Chip, CircularProgress } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import MicIcon from "@mui/icons-material/Mic";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BlockIcon from "@mui/icons-material/Block";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { reTranscribeCall, checkCallAllowed } from "../utils/firebase";
import CircularGauge from "./CircularGauge";

function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_KEYS = {
  pending: "pending",
  scheduled: "scheduled",
  bot_sent: "bot_sent",
  recording: "recording",
  bot_failed: "bot_failed",
  transcribing: "transcribing",
  analyzing: "analyzing",
  completed: "completed",
  no_transcript: "no_transcript",
  no_show: "no_show",
  failed: "failed",
};

const STATUS_COLORS = {
  pending: "default",
  scheduled: "default",
  bot_sent: "info",
  recording: "success",
  bot_failed: "error",
  transcribing: "info",
  analyzing: "warning",
  completed: "success",
  no_transcript: "warning",
  no_show: "warning",
  failed: "error",
};

export default function CallCard({ call, onRefresh, canManageMeetings = true, onLimitReached, memberId = null, isSample = false, sampleVariant = "strong" }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { localizePath } = useLocale();
  const [reAnalyzing, setReAnalyzing] = useState(false);
  const [reAnalyzeError, setReAnalyzeError] = useState(null);
  const { id, title, timestamp, score, summary, status, error, errorCode, talkRatio, questionCount, interruptionRate, silenceRatio, nextSteps } = call;
  // Prefer a stable, localized message keyed by errorCode; fall back to the raw
  // backend error only when no code is present (older records).
  const errorMessage = errorCode
    ? t(`callCard.errors.${errorCode}`, { defaultValue: t("callCard.errors.sync_failed") })
    : error;
  const statusKey = STATUS_KEYS[status] || "pending";
  const statusLabel = t(`callCard.status.${statusKey}`);
  const statusColor = STATUS_COLORS[status] || "default";
  const tr = talkRatio && typeof talkRatio === "object" ? talkRatio : null;
  const qc = questionCount && typeof questionCount === "object" ? questionCount : null;
  const ir = interruptionRate && typeof interruptionRate === "object" ? interruptionRate : null;
  const sr = silenceRatio && typeof silenceRatio === "object" ? silenceRatio : null;
  const ns = nextSteps && typeof nextSteps === "object" ? nextSteps : null;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box>
            <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 15 }}>
              {title || t("callCard.untitledMeeting")}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", fontSize: 13 }}>
              {formatDate(timestamp)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            {isSample && (
              <Chip
                label={t("callCard.sampleBadge")}
                size="small"
                sx={{ bgcolor: "#eef2ff", color: "#4338ca", fontWeight: 600, fontSize: 11 }}
              />
            )}
            {status !== "completed" && (
              <Chip label={statusLabel} size="small" color={statusColor} />
            )}
            {status === "completed" && score != null && (
              <CircularGauge score={score} size={42} strokeWidth={3} />
            )}
          </Box>
        </Box>

        {tr && status === "completed" && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ mb: 1.5, flexWrap: "wrap" }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                bgcolor: tr.inSweetSpot ? "rgba(16, 185, 129, 0.05)" : "rgba(107, 114, 128, 0.03)",
                color: tr.inSweetSpot ? "#059669" : "#6b7280",
                border: tr.inSweetSpot ? "1px solid rgba(16, 185, 129, 0.15)" : "1px solid rgba(107, 114, 128, 0.1)",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <MicIcon sx={{ fontSize: 16 }} />
              Talk: {tr.repTalkPct}%
            </Box>
            {tr.inSweetSpot ? (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  fontSize: 12,
                  color: "#059669",
                  fontWeight: 500,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 16 }} />
                {t("callCard.sweetSpot")} ({tr.sweetSpotRange})
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                {t("callCard.aimFor")} {tr.sweetSpotRange}
              </Typography>
            )}
          </Box>
        )}

        {(qc || ir || sr || ns) && status === "completed" && (
          <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
            {qc && (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: qc.inTarget ? "rgba(16, 185, 129, 0.05)" : "rgba(107, 114, 128, 0.03)",
                  color: qc.inTarget ? "#059669" : "#6b7280",
                  border: qc.inTarget ? "1px solid rgba(16, 185, 129, 0.15)" : "1px solid rgba(107, 114, 128, 0.1)",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 16 }} />
                {t("callCard.discoveryQuestions")}: {qc.count}
                {qc.inTarget && <CheckCircleIcon sx={{ fontSize: 14 }} />}
              </Box>
            )}
            {ir && (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: ir.ratePct < 10 ? "rgba(16, 185, 129, 0.05)" : "rgba(107, 114, 128, 0.03)",
                  color: ir.ratePct < 10 ? "#059669" : "#6b7280",
                  border: ir.ratePct < 10 ? "1px solid rgba(16, 185, 129, 0.15)" : "1px solid rgba(107, 114, 128, 0.1)",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <BlockIcon sx={{ fontSize: 16 }} />
                Interruptions: {ir.ratePct}%
                {ir.ratePct < 10 && <CheckCircleIcon sx={{ fontSize: 14 }} />}
              </Box>
            )}
            {sr && (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: sr.inSweetSpot ? "rgba(16, 185, 129, 0.05)" : "rgba(107, 114, 128, 0.03)",
                  color: sr.inSweetSpot ? "#059669" : "#6b7280",
                  border: sr.inSweetSpot ? "1px solid rgba(16, 185, 129, 0.15)" : "1px solid rgba(107, 114, 128, 0.1)",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <PauseCircleOutlineIcon sx={{ fontSize: 16 }} />
                Pause: {sr.silencePct}%
                {sr.inSweetSpot && <CheckCircleIcon sx={{ fontSize: 14 }} />}
              </Box>
            )}
            {ns && (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: ns.hasNextSteps ? "rgba(16, 185, 129, 0.05)" : "rgba(107, 114, 128, 0.03)",
                  color: ns.hasNextSteps ? "#059669" : "#6b7280",
                  border: ns.hasNextSteps ? "1px solid rgba(16, 185, 129, 0.15)" : "1px solid rgba(107, 114, 128, 0.1)",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <PlaylistAddCheckIcon sx={{ fontSize: 16 }} />
                {ns.hasNextSteps ? t("callCard.nextStepsYes") : t("callCard.nextStepsNo")}
                {ns.hasNextSteps && <CheckCircleIcon sx={{ fontSize: 14 }} />}
              </Box>
            )}
          </Box>
        )}

        {summary && (
          <Typography sx={{ color: "#6b7280", fontSize: 14, mb: 2 }}>
            {summary}
          </Typography>
        )}

        {!summary && errorMessage && (
          <Typography sx={{ color: "#9ca3af", fontSize: 13, mb: 2 }}>
            {errorMessage}
          </Typography>
        )}

        {status === "completed" && (
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() =>
                navigate(
                  localizePath(
                    isSample
                      ? `/call/sample?variant=${sampleVariant}`
                      : `/call/${id}${memberId ? `?memberId=${memberId}` : ""}`
                  )
                )
              }
              sx={{ textTransform: "none" }}
            >
              {t("callCard.viewDetails")}
            </Button>
            {canManageMeetings && !isSample && (
            <Button
              size="small"
              variant="outlined"
              startIcon={reAnalyzing ? <CircularProgress size={16} /> : <RefreshIcon />}
              disabled={reAnalyzing}
              onClick={async () => {
                setReAnalyzeError(null);
                if (onLimitReached) {
                  try {
                    const checkResult = await checkCallAllowed();
                    if (!checkResult.data?.allowed) {
                      onLimitReached();
                      return;
                    }
                  } catch {
                    // If check fails, proceed with re-transcribe
                  }
                }
                setReAnalyzing(true);
                try {
                  await reTranscribeCall({ callId: id });
                  onRefresh?.();
                } catch (err) {
                  setReAnalyzeError(err.message || t("callCard.reAnalyzeFailed"));
                } finally {
                  setReAnalyzing(false);
                }
              }}
              sx={{ textTransform: "none" }}
            >
              {reAnalyzing ? t("callCard.reAnalyzing") : t("callCard.reAnalyze")}
            </Button>
            )}
          </Box>
        )}
        {reAnalyzeError && (
          <Typography sx={{ color: "#dc2626", fontSize: 13, mt: 1 }}>
            {reAnalyzeError}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
